// @ts-check

// Tradeshow flag rules shared by the main search and org profile reducers so
// the two pages cannot drift.

/** The roles "Show all attending presenters" selects. */
export const ATTENDING_PRESENTER_ROLES = [
  'Abstract Presenter',
  'Oral Presenter',
  'Poster Presenter',
  'Keynote Speaker',
  'Invited Speaker',
  'Plenary Speaker',
  'Platform Speaker',
  'Panel Speaker',
  'Speaker',
  'Instructor',
];

/**
 * Mutates `next` in place after the caller has set or deleted `next[id]`.
 * @param {Record<string, any>} prev filters before this change
 * @param {Record<string, any>} next filters after this change
 * @param {string} id the filter key that changed
 */
export const applyTradeshowRules = (prev, next, id) => {
  if (id === 'tradeshow') {
    const hadTradeshow = Boolean(prev.tradeshow?.length);
    const hasTradeshow = Boolean(next.tradeshow?.length);

    if (!hasTradeshow) {
      delete next.thisShowOnly;
      delete next.exhibitingShow;
      delete next.presentingAtTradeshow;
    }
    // Default only on the first selection, and only while no mode is decided,
    // so later edits never override the user's own choice.
    if (
      hasTradeshow &&
      !hadTradeshow &&
      !next.thisShowOnly &&
      !next.exhibitingShow &&
      !next.presentingAtTradeshow
    ) {
      next.thisShowOnly = true;
    }
  }

  if (id === 'sessionType' && !next.sessionType?.length) {
    delete next.thisSessionTypeOnly;
  }

  if (id === 'roleType') {
    const hadRoleType = Boolean(prev.roleType?.length);
    const hasRoleType = Boolean(next.roleType?.length);

    if (!hasRoleType) {
      delete next.thisRoleTypeOnly;
    }
    // Only when nothing has decided it yet: a bulk apply (SciAI Search, alerts,
    // URL hydration) never comes through here, so the AI's deliberate
    // `thisRoleTypeOnly: false` (role as career history) is left alone.
    if (hasRoleType && !hadRoleType && next.thisRoleTypeOnly === undefined) {
      next.thisRoleTypeOnly = true;
    }
  }

  if (id === 'thisShowOnly' && next.thisShowOnly) {
    delete next.exhibitingShow;
    delete next.presentingAtTradeshow;
  }
  // Unticking "this show only" with no broad mode left gives the tradeshow
  // nothing to apply to, so fall back to Presenting, the closest broad mode.
  if (
    prev.thisShowOnly &&
    !next.thisShowOnly &&
    next.tradeshow?.length &&
    !next.exhibitingShow &&
    !next.presentingAtTradeshow
  ) {
    next.presentingAtTradeshow = true;
  }
};

/**
 * Main search ticks "only this session type" on the first session type pick
 * from an effect in Search/Filters/Tradeshows.js; the org profile has no such
 * effect, so it does it here. The first tradeshow pick's default is in
 * `applyTradeshowRules`, for both pages.
 * Mutates `next` in place, after `applyTradeshowRules`.
 * @param {Record<string, any>} prev filters before this change
 * @param {Record<string, any>} next filters after this change
 * @param {string} id the filter key that changed
 */
export const applyFirstPickOnlyDefaults = (prev, next, id) => {
  if (
    id === 'sessionType' &&
    !prev.sessionType?.length &&
    next.sessionType?.length
  ) {
    next.thisSessionTypeOnly = true;
  }
};

/**
 * A tradeshow is picked but none of exhibiting / presenting / only-this-show
 * is set, so the query has nothing to apply the tradeshow to.
 * @param {Record<string, any>} filters
 */
export const isMissingTradeshowFlag = filters =>
  Boolean(filters.tradeshow?.length) &&
  !(
    filters.exhibitingShow ||
    filters.presentingAtTradeshow ||
    filters.thisShowOnly
  );
